import React from "react";
import { useTranslation } from "react-i18next";
import useScrollAnimation from "@/hooks/useScrollAnimation";
import "./CapabilitiesSection.scss";

const BrainIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
    <path d="M9 3a3 3 0 0 0-3 3 3 3 0 0 0-3 3c0 1.2.7 2.2 1.7 2.7A3 3 0 0 0 6 15c0 1.7 1.3 3 3 3h3V3H9z"/>
    <path d="M15 3a3 3 0 0 1 3 3 3 3 0 0 1 3 3c0 1.2-.7 2.2-1.7 2.7A3 3 0 0 1 18 15c0 1.7-1.3 3-3 3h-3V3h3z"/>
    <path d="M12 8v2M12 13v2"/>
  </svg>
);

const CodeIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
    <path d="M8 6l-6 6 6 6M16 6l6 6-6 6M14 4l-4 16"/>
  </svg>
);

const DataIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
    <ellipse cx="12" cy="5" rx="8" ry="3"/>
    <path d="M4 5v6c0 1.7 3.6 3 8 3s8-1.3 8-3V5"/>
    <path d="M4 11v6c0 1.7 3.6 3 8 3s8-1.3 8-3v-6"/>
  </svg>
);

const CloudIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
    <path d="M18 17a4 4 0 0 0 0-8 6 6 0 0 0-11.8-1A4.5 4.5 0 0 0 6.5 17H18z"/>
  </svg>
);

const ArrowDiagIcon = () => (
  <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
    <path d="M5 11L11 5M6 5h5v5"/>
  </svg>
);

const CAPS = [
  { key: "aiMl", icon: <BrainIcon />, num: "01" },
  { key: "customSoftware", icon: <CodeIcon />, num: "02" },
  { key: "dataEngineering", icon: <DataIcon />, num: "03" },
  { key: "cloud", icon: <CloudIcon />, num: "04" },
] as const;

const CapabilitiesSection: React.FC = () => {
  const { t } = useTranslation();
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.1 });

  return (
    <section className="cap-section" ref={ref}>
      <div id="capabilities-section" className="container">
        <div className={`sec-head reveal${isVisible ? " in" : ""}`}>
          <span className="sec-label">{t("capabilities.label")}</span>
          <h2 className="sec-title">
            {(() => {
              const title = t("capabilities.title");
              const hl = t("capabilities.titleHighlight");
              const [before, after = ""] = title.split(hl);
              return <>{before}<em>{hl}</em>{after}</>;
            })()}
          </h2>
        </div>
      </div>
      <div className="container" style={{ paddingLeft: 0, paddingRight: 0, maxWidth: "none" }}>
        <div className="container" style={{ padding: 0 }}>
          <div className={`cap-grid reveal${isVisible ? " in" : ""}`}>
            {CAPS.map((item) => (
              <div className="cap-cell" key={item.key}>
                <div className="cap-num">{item.num}</div>
                <div className="cap-icon">{item.icon}</div>
                <h3 className="cap-title">{t(`capabilities.${item.key}.title`)}</h3>
                <p className="cap-desc">{t(`capabilities.${item.key}.description`)}</p>
                <span className="cap-arrow">
                  {t("capabilities.learnMore")} <ArrowDiagIcon />
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CapabilitiesSection;
